import { type ReactNode } from 'react'

import { Space, Text } from '@procurify/ui'
import { type FormatDateOptions } from 'react-intl'

import { type CurrencyName } from '@webapp/core/entities/external/Currency/CurrencyName'
import { useProcurifyIntl } from '@webapp/hooks'
import { ConversationOrderSummaryDeliveryLine } from './ConversationOrderSummaryDeliveryLine'
import { ConversationOrderSummarySection } from './ConversationOrderSummarySection'
import { type IConversationVendorOrderSummaryItem } from './ConversationVendorOrderSummary'
import messages from './messages'
import { ConversationOrderSummaryValueStyled } from './styles/ConversationOrderSummaryValueStyled'
import { type ConversationCartItemDelivery } from '../../value-objects/ConversationTurnItem'

const DELIVERY_DATE_FORMAT: FormatDateOptions = {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
}

export interface IConversationOrderSummaryDeliveryItem extends IConversationVendorOrderSummaryItem {
  delivery?: ConversationCartItemDelivery
}

export interface IConversationOrderSummaryDelivery {
  id: string
  estimatedDeliveryDate?: string
  items: IConversationOrderSummaryDeliveryItem[]
}

export interface IConversationOrderSummaryDeliveryProps {
  delivery: IConversationOrderSummaryDelivery
  position: number
  currency: CurrencyName
  isCollapsible: boolean
  isLineCentered?: boolean
  renderQuantity: (item: IConversationOrderSummaryDeliveryItem) => ReactNode
}

export const ConversationOrderSummaryDelivery = ({
  delivery,
  position,
  currency,
  isCollapsible,
  isLineCentered,
  renderQuantity,
}: IConversationOrderSummaryDeliveryProps) => {
  const intl = useProcurifyIntl()
  const heading = intl.formatMessage(messages.deliveryHeading, {
    index: position,
  })

  const renderEstimate = () => {
    if (!delivery.estimatedDeliveryDate) return null
    return (
      <Text inline color='textSecondary'>
        {intl.formatMessage(messages.deliverBy, {
          date: intl.formatDate(
            delivery.estimatedDeliveryDate,
            DELIVERY_DATE_FORMAT
          ),
        })}
      </Text>
    )
  }

  return (
    <ConversationOrderSummarySection
      ariaLabel={heading}
      isCollapsible={isCollapsible}
      title={
        <Space direction='horizontal' justify='space-between' align='center'>
          <Space direction='horizontal' size='xsmall' align='center'>
            <ConversationOrderSummaryValueStyled $isBold>
              {heading}
            </ConversationOrderSummaryValueStyled>
            <Text inline color='textSecondary'>
              {intl.formatMessage(messages.deliveryItemCount, {
                count: delivery.items.length,
              })}
            </Text>
          </Space>
          {renderEstimate()}
        </Space>
      }
    >
      {delivery.items.map((item) => (
        <ConversationOrderSummaryDeliveryLine
          key={item.id}
          item={item}
          currency={currency}
          isCentered={isLineCentered}
          quantity={renderQuantity(item)}
        />
      ))}
    </ConversationOrderSummarySection>
  )
}
